import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Skeleton from '@mui/material/Skeleton'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import IconButton from '@mui/material/IconButton'
import Share from '@mui/icons-material/Share'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { renderTabContent } from './TabContent'
import { tabs } from './tabs'
import { getRace } from '../../api/races'
import RaceHeader from './RaceHeader.js'
import HelmetMeta from '../../layouts/HelmetMeta'
import { localizeContent } from '../../i18n/localizeContent'

/*
  An event can be in the phases:
  - registrationsClosed: true / false
  - status: one of ['open', 'soon', 'later', 'closed', 'ended']
    - open - in this stage, registrations, live, results cand be on at various times.
    - soon - registrations, live, results are closed
    - later - registrations, live, results are closed
    - closed - registrations, live, results are closed. The event might have been closed before the event time.
    - ended - registrations, live are closed. Results might still be open. The event took place and ended.

  Tabs may be controlled by the event state or by flags coming from the DB, such as: showResults, showLive, showRegistration, showMedia
*/

const RacePage = () => {
  const { t, i18n } = useTranslation('race')
  const { name, section, category, item } = useParams()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('details')
  const [rawRace, setRawRace] = useState(null)
  const [raceLoading, setRaceLoading] = useState(true)
  const today = dayjs()
  const lang = i18n.resolvedLanguage || i18n.language || 'en'
  const raceData = useMemo(() => localizeContent(rawRace, lang), [rawRace, lang])

  useEffect(() => {
    const offset = -160 // adjust this value

    const scrollToTabPage = (behavior) => {
      // Re-query the element on every call: it is briefly unmounted while the
      // route changes, so a cached reference would report a zero rect.
      const element = document.getElementById('tab-page')
      if (!element) return

      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset + offset

      window.scrollTo({
        top: offsetPosition,
        behavior
      })
    }

    scrollToTabPage('smooth')

    // The new tab content can still be mounting (images, embeds, accordions),
    // which briefly makes the page too short to reach the target and leaves the
    // scroll clamped. Keep re-applying the target while the layout settles.
    let stopped = false
    let correcting = false
    const observer = new ResizeObserver(() => {
      if (correcting) scrollToTabPage('auto')
    })
    observer.observe(document.body)

    const stop = () => {
      if (stopped) return
      stopped = true
      observer.disconnect()
      window.removeEventListener('wheel', stop)
      window.removeEventListener('touchstart', stop)
    }

    const startCorrecting = setTimeout(() => {
      if (stopped) return
      correcting = true
      scrollToTabPage('auto')
    }, 400)

    const timeout = setTimeout(stop, 2500)
    window.addEventListener('wheel', stop, { passive: true })
    window.addEventListener('touchstart', stop, { passive: true })

    return () => {
      clearTimeout(startCorrecting)
      clearTimeout(timeout)
      stop()
    }
  }, [pathname])

  useEffect(() => {
    let active = true
    if (!name) {
      navigate('/not-found')
      return
    }
    setRaceLoading(true)
    getRace(name)
      .then(race => { if (active) setRawRace(race) })
      .catch(() => { if (active) navigate('/not-found') })
      .finally(() => { if (active) setRaceLoading(false) })
    return () => { active = false }
  }, [name, navigate])

  // Compute filtered tabs outside useEffect so we can use it in redirect logic
  const filteredTabs = useMemo(() => tabs.filter(tab => {
    // Map tabs to their corresponding show flags
    const tabToFlagMap = {
      participants: 'showParticipants',
      media: 'showMedia',
      registration: 'showRegistration',
      live: 'showLive',
      results: 'showResults',
      welcome: 'showWelcome',
      details: 'showDetails',
      policies: 'showPolicies',
      volunteers: 'showVolunteers',
      contact: 'showContact',
      links: 'showLinks'
    }

    const flag = tabToFlagMap[tab]
    // If no flag mapping exists, show the tab by default
    if (flag && raceData?.[flag] === false) return false

    // When status is 'later', hide certain tabs
    if (raceData?.status === 'later') {
      const tabsHiddenOnLater = ['media', 'welcome', 'participants', 'live', 'results']
      if (tabsHiddenOnLater.includes(tab)) {
        return false
      }
    }

    // When status is 'ended', hide certain tabs unless their flag is explicitly true
    if (raceData?.status === 'ended') {
      const tabsHiddenOnEnded = ['participants', 'live', 'registration', 'details', 'policies', 'volunteers', 'contact']
      if (tabsHiddenOnEnded.includes(tab)) {
        // Show only if the flag is explicitly true
        return raceData?.[flag] === true
      }
    }

    return true
  }), [tabs, raceData?.status])

  // Add 'welcome' tab before 'details' if welcome key exists in raceData
  const displayTabs = useMemo(() => {
    if (!raceData?.welcome || raceData.showWelcome === false) return filteredTabs
    const welcomeTabIndex = filteredTabs.indexOf('details')
    if (welcomeTabIndex !== -1) {
      const result = [...filteredTabs]
      result.splice(welcomeTabIndex, 0, 'welcome')
      return result
    }
    return filteredTabs
  }, [filteredTabs, raceData?.welcome, raceData?.showWelcome])

  // Redirect to first available tab if current section is not displayed
  useEffect(() => {
    if (name && section && raceData && displayTabs.length > 0) {
      const registrationUnavailable = section === 'registration' && (
        !displayTabs.includes('registration') ||
        raceData.status !== 'open' ||
        raceData.registrationsClosed
      )

      if (registrationUnavailable) {
        const target = displayTabs.includes('details') ? 'details' : displayTabs[0]
        navigate(`/races/${name}/${target}`, { replace: true })
      } else if (!displayTabs.includes(section)) {
        // Redirect to first available tab
        navigate(`/races/${name}/${displayTabs[0]}`, { replace: true })
      } else {
        setActiveTab(section)
      }
    }
  }, [name, section, raceData, displayTabs, navigate])

  const handleTab = tab => {
    navigate(`/races/${raceData.slug}/${tab}`)
  }

  const handleShare = async () => {
    const url = window.location.href

    if (navigator.share) {
      try {
        await navigator.share({ title: raceData?.title || t('fallbackTitle'), url })
      } catch (err) {
        console.log('Share failed:', err)
      }
    } else {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(url).then(
          () => {
            alert(t('share.copied'))
          },
          (err) => {
            console.error('Failed to copy: ', err)
          }
        )
      } else {
        const textArea = document.createElement('textarea')
        textArea.value = url
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        alert(t('share.copied'))
      }
    }
  }

  // const raceDate = dayjs(raceData.date[0])
  // const secondDate = raceData.date.length > 1 ? dayjs(raceData.date[1]) : null
  // Determine the first and last event dates even when there is only one date
  const firstEventDate = dayjs(raceData?.date[0])
  const lastEventDate = raceData?.date.length > 1 ? dayjs(raceData?.date[1]) : firstEventDate

  const isTabDisabled = (tabLabel) => {
    const raceDate = dayjs(raceData.date[0])
    const secondDate = raceData.date.length > 1 ? dayjs(raceData.date[1]) : null

    // console.log(today)

    // Determine the first and last event dates
    const firstEventDate = raceDate
    const lastEventDate = secondDate || raceDate
    const liveEnabledTime = firstEventDate.subtract(1, 'day').hour(22).minute(0).second(0)
    if (tabLabel === 'registration') {
      // Registration is only enabled when status is 'open'
      return raceData.status !== 'open'
    }

    if (tabLabel === 'live') {
      // Live is enabled during event days when status is 'open'
      if (raceData.status !== 'open') return true
      // Enable live tab the night before first day at 10PM local time

      const liveEndTime = lastEventDate.endOf('day')
      // console.log({
      //   1: !today.isAfter(liveEnabledTime),
      //   2: (today.isBefore(liveEndTime) || today.isSame(liveEndTime)),
      //   3: today.isBefore(liveEndTime),
      //   4: today.isSame(liveEndTime),
      //   liveEndTime
      // })
      return !(today.isAfter(liveEnabledTime) && today.isBefore(liveEndTime))
    }
    if (tabLabel === 'results') {
      // Results are enabled on or after the last event date
      return today.isBefore(liveEnabledTime.startOf('day'))
    }
    // For other tabs (participants, etc.), use existing logic
    // if (['soon', 'later', 'ended', 'closed'].includes(raceData.status) && ['registration', 'live', 'participants'].includes(tabLabel)) return true
    return false
  }

  if (raceLoading || !raceData) {
    return (
      <Container maxWidth='xl' sx={{ mt: 8 }}>
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Skeleton variant='rectangular' height={320} />
          <Skeleton variant='rectangular' height={60} />
          <Skeleton variant='rectangular' height={400} />
        </Box>
      </Container>
    )
  }

  return (
    <>
      <Container maxWidth='xl' disableGutters sx={{ /* pt: { xs: 7, md: 12 },  position: 'relative' */ }}>
        <HelmetMeta
          {...{
          title: raceData.title,
          description: raceData.subtitle,
          image: raceData.image || `/img/races/i/${raceData.slug}.jpg`
        }}
        />
        {/* Share button container */}
        <Box sx={{ position: 'absolute', right: 20, top: 64, zIndex: 10 }}>
          <IconButton onClick={() => handleShare()} sx={{ bgcolor: 'rgba(0,0,0,0.5)', color: 'white', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }} size='large'>
            <Share />
          </IconButton>
        </Box>
        <Box sx={{ position: 'relative', minHeight: 320 }}>
          <RaceHeader {...{ raceData }}/>
        </Box>
      </Container>
      <Container maxWidth='xl' disableGutters sx={{ width: '100%', mt: 4, position: 'sticky', top: { xs: 56, md: 82 }, zIndex: 10, bgcolor: '#333' }}>
        {/* Tabs */}
        <Tabs
          value={displayTabs.indexOf(activeTab) > -1 ? displayTabs.indexOf(activeTab) : 0}
          onChange={(e, newValue) => {
            handleTab(displayTabs[newValue])
          }}
          // centered
          variant='scrollable'
          scrollButtons={true}
          allowScrollButtonsMobile
          indicatorColor='secondary'
        >
          {displayTabs.map((tabLabel) => (
            <Tab
              key={tabLabel}
              label={t('tabs.' + tabLabel)}
              disabled={isTabDisabled(tabLabel)}
              sx={{
                opacity: isTabDisabled(tabLabel) ? 0.5 : 1,
                transition: 'opacity 0.3s ease'
              }}
            />
          ))}
        </Tabs>
      </Container>
      {/* Tab Content */}
      <Box sx={{ mt: 4, minHeight: '70vh' }} id='tab-page'>
        {renderTabContent({ activeTab, eventDetails: { ...raceData, firstEventDate, lastEventDate }, isTabDisabled, today, category, item })}
      </Box>
    </>
  )
}

export default RacePage
